import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import SignaturePad from "react-signature-canvas";
import "../styles/Signature.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const SupervisorSignatureManager = ({ taskId, taskName, userId, referenceId = null, strictReference = false, refreshTrigger, onSignatureChange, canSign = false }) => {
  const sigPadRef = useRef(null);
  const [savedSignature, setSavedSignature] = useState(null);
  const [signedDate, setSignedDate] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchSignature = useCallback(async () => {
    if (strictReference && referenceId == null) {
      setSavedSignature(null); setSignedDate(null); onSignatureChange?.(false); return;
    }
    try {
      const res = await axios.get(`${API_BASE}/api/usertasks/status/${userId}`, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
      const suffix = referenceId != null ? `_${referenceId}` : "";
      const idKey = taskId != null ? `${taskId}${suffix}` : null;
      const nameKey = taskName ? `${taskName}${suffix}` : null;
      const signature = (idKey && res.data.supervisorSignatures?.[idKey]) ?? (nameKey && res.data.supervisorSignatures?.[nameKey]) ?? null;
      const date = (idKey && res.data.supervisorSigndates?.[idKey]) ?? (nameKey && res.data.supervisorSigndates?.[nameKey]) ?? null;
      setSavedSignature(signature); setSignedDate(date); onSignatureChange?.(Boolean(signature));
    } catch (error) { console.error("Failed to load supervisor signature", error); }
  }, [onSignatureChange, referenceId, strictReference, taskId, taskName, userId]);

  useEffect(() => { fetchSignature(); }, [fetchSignature, refreshTrigger]);

  const saveSignature = async () => {
    if (!canSign) return;
    if (!sigPadRef.current || sigPadRef.current.isEmpty()) { alert("Please draw the supervisor signature first."); return; }
    try {
      setSaving(true);
      const signature = sigPadRef.current.getCanvas().toDataURL("image/png");
      const res = await axios.post(`${API_BASE}/api/usertasks/supervisor-sign`, { userId, taskId, taskName, referenceId, signature }, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
      setSavedSignature(signature); setSignedDate(res.data.signedDate || new Date().toISOString()); onSignatureChange?.(true);
    } catch (error) { alert(error.response?.data?.message || "Failed to save supervisor signature."); }
    finally { setSaving(false); }
  };

  const redoSignature = async () => {
    if (!canSign || !savedSignature) return;
    try {
      setSaving(true);
      await axios.post(`${API_BASE}/api/usertasks/supervisor-reset`, { userId, taskId, taskName, referenceId }, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
      setSavedSignature(null); setSignedDate(null); onSignatureChange?.(false);
    } catch (error) { alert(error.response?.data?.message || "Failed to reset supervisor signature."); }
    finally { setSaving(false); }
  };

  return <div className="mt-4 supervisor-signature-section">
    <label className="form-label">Supervisor Signature:</label>
    <div className="signature-container" data-download-media-frame>
      {savedSignature ? <><img src={savedSignature} alt="Supervisor signature" className="signature-image" data-download-preserve-ratio />
        {signedDate && <small className="text-muted mt-2">Signed {new Date(signedDate).toLocaleString()}</small>}
        {canSign && <button type="button" className="btn btn-secondary mt-2" disabled={saving} onClick={redoSignature} data-download-exclude>{saving ? "Resetting..." : "Re-do Signature"}</button>}</> : <>
        <SignaturePad ref={sigPadRef} canvasProps={{ className: `sigCanvas${canSign ? "" : " signature-readonly"}`, "data-download-preserve-ratio": true, "aria-label": canSign ? "Draw supervisor signature" : "Supervisor signature is read only" }} />
        <div className="signature-buttons mt-2" data-download-exclude>
          <button type="button" className="btn btn-secondary" disabled={!canSign} onClick={() => sigPadRef.current?.clear()}>Clear Signature</button>
          <button type="button" className="btn btn-success" disabled={saving || !canSign} onClick={saveSignature}>{saving ? "Saving..." : "Save Supervisor Signature"}</button>
        </div></>}
    </div>
  </div>;
};

export default SupervisorSignatureManager;
